// 工作台 / 项目列表：保留 API 与任务逻辑，统一策略咖品牌视觉。
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import { api, type ProjectListItem } from "../lib/api";
import SlideCanvas from "../components/SlideCanvas";
import { QualityBadge } from "../components/QualityBadge";
import { BrandLogo } from "../components/BrandLogo";
import "./Projects.css";

const TERMINAL = ["done", "failed", "canceled"];
const STATUS_LABEL: Record<string, string> = { queued: "排队中", intake: "解析资料", research: "行业调研", strategy: "制定策略", financial: "经营测算", outline: "生成大纲", drafting: "撰写文案", review: "评审打磨", assemble: "装配 PPT", done: "已完成", failed: "失败", canceled: "已取消" };
const PREVIEWS = ["/assets/landing/deck-signal.svg", "/assets/landing/deck-direction.svg", "/assets/landing/deck-growth.svg"];

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function latest(p: ProjectListItem) {
  const jobs = [...(p.jobs ?? [])].sort((a, b) => b.created_at.localeCompare(a.created_at));
  const decks = [...(p.decks ?? [])].sort((a, b) => b.created_at.localeCompare(a.created_at));
  return { job: jobs[0] ?? null, deck: decks[0] ?? null };
}

function ProjectCard({ p, index, onRegen, busy }: { p: ProjectListItem; index: number; onRegen: (id: string) => void; busy: boolean }) {
  const { job, deck } = useMemo(() => latest(p), [p]);
  const running = Boolean(job && !TERMINAL.includes(job.status));
  const state = running ? "running" : job?.status === "failed" ? "failed" : deck ? "done" : "draft";
  const stateLabel = running ? STATUS_LABEL[job?.status ?? "queued"] : job?.status === "failed" ? "生成失败" : deck ? "已出片" : "待生成";

  return (
    <article className={`aim-project-card is-${state}`}>
      <div className="aim-project-preview">
        {deck?.cover
          ? <div className="aim-project-cover"><SlideCanvas slide={deck.cover} pageIdx={1} style={deck.style} themeId={deck.theme_id} totalPages={1} /></div>
          : <img src={PREVIEWS[index % PREVIEWS.length]} alt="" />}
        <span>{String(index + 1).padStart(2, "0")}</span><b>{stateLabel}</b>
      </div>
      <div className="aim-project-body">
        <div className="aim-project-title"><div><p>{p.industry ?? "未分类"}</p><h2 title={p.name}>{p.name}</h2></div><div className="aim-project-badges"><QualityBadge qualityStatus={job?.quality_status} jobStatus={job?.status} /><span aria-hidden="true">↗</span></div></div>
        <dl><div><dt>创建</dt><dd>{fmtDate(p.created_at)}</dd></div><div><dt>状态</dt><dd>{running ? `${job?.progress ?? 0}%` : deck ? `最近出片 ${fmtDate(deck.created_at)}` : "尚未启动"}</dd></div></dl>
        {job?.status === "failed" && <p className="aim-project-warning">上次生成失败，可重新启动。</p>}
        <div className="aim-project-actions">
          {deck ? <><Link className="is-primary" to={`/deck/${deck.id}`}>查看结果 <span>↗</span></Link><Link to={`/deck/${deck.id}/edit`}>继续编辑</Link>{job && <Link to={`/job/${job.id}`} title="回看这次生成的完整过程与各工具产物">生成过程 <span>↗</span></Link>}</> : running && job ? <Link className="is-primary" to={`/job/${job.id}`}>查看进度 <span>↗</span></Link> : <button className="is-primary" disabled={busy} onClick={() => onRegen(p.id)}>{busy ? "启动中…" : "开始生成"}<span>↗</span></button>}
          {deck && <button disabled={busy} onClick={() => onRegen(p.id)}>{busy ? "启动中…" : "重新生成"}</button>}
        </div>
      </div>
    </article>
  );
}

export default function Projects() {
  const { user, signOut } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState<ProjectListItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const loadProjects = useCallback(() => {
    setItems(null); setErr(null);
    api.listProjects().then((r) => setItems(r.projects)).catch((error) => setErr((error as Error).message));
  }, []);
  useEffect(() => loadProjects(), [loadProjects]);

  async function regen(projectId: string) {
    setBusyId(projectId); setErr(null);
    try { const { job_id } = await api.startJob(projectId); nav(`/job/${job_id}`); }
    catch (error) { setErr((error as Error).message); setBusyId(null); }
  }

  const counts = useMemo(() => {
    const all = items ?? [];
    return { total: all.length, ready: all.filter((p) => latest(p).deck).length, running: all.filter((p) => { const j = latest(p).job; return j && !TERMINAL.includes(j.status); }).length };
  }, [items]);

  return (
    <div className="aim-projects">
      <header className="aim-projects-header"><Link className="aim-projects-wordmark" to="/" aria-label="策略咖首页"><BrandLogo /></Link><nav><Link to="/admin">成本看板</Link><span>{user?.email}</span><button onClick={() => signOut()}>退出 <i aria-hidden="true">↗</i></button></nav></header>
      <main>
        <section className="aim-workspace-head"><div><p className="aim-projects-eyebrow">YOUR WORKSPACE / PROJECTS</p><h1>你的营销方案，<br /><em>集中在这里。</em></h1><p className="aim-workspace-lede">从品牌资料到最终提案，继续编辑已有项目，或开始下一套方案。</p></div><div className="aim-workspace-signal" aria-hidden="true"><i /><i /><strong>{String(counts.total).padStart(2, "0")}</strong></div></section>
        <section className="aim-projects-toolbar"><div className="aim-projects-stats"><p><strong>{counts.total}</strong><span>全部项目</span></p><p><strong>{counts.ready}</strong><span>已完成</span></p><p><strong>{counts.running}</strong><span>生成中</span></p></div><Link className="aim-new-project" to="/new"><span>＋</span> 新建方案 <b aria-hidden="true">↗</b></Link></section>

        <section className="aim-projects-content" aria-label="项目列表">
          <div className="aim-projects-section-title"><p className="aim-projects-eyebrow">RECENT PROJECTS</p><span>{items ? `${items.length} ITEMS` : "SYNCING"}</span></div>
          {err && <div className="aim-projects-error" role="alert"><div><strong>项目数据暂时无法读取</strong><p>{err}</p></div><button onClick={loadProjects}>重新加载 <span>↗</span></button></div>}
          {!items && !err && <div className="aim-projects-loading"><i /><p>正在同步你的工作空间…</p></div>}
          {items?.length === 0 && <div className="aim-projects-empty"><p className="aim-projects-eyebrow">NO PROJECTS YET</p><h2>从第一份品牌资料开始。</h2><p>创建项目后，AI 将完成调研、策略、文案与 PPT 装配。</p><Link to="/new">创建第一个方案 <span>↗</span></Link></div>}
          {items && items.length > 0 && <div className="aim-projects-grid">{items.map((p, index) => <ProjectCard key={p.id} p={p} index={index} onRegen={regen} busy={busyId === p.id} />)}</div>}
        </section>
      </main>
    </div>
  );
}
